import { TranslationKeys } from '@/localization';
import { ComponentProps, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

type TabProps = {
  index: number;
  tabListId: string;
  isSelected: boolean;
  disabled?: boolean;
  onClick: ComponentProps<'button'>['onClick'];
  onKeyDown: ComponentProps<'button'>['onKeyDown'];
  title: TranslationKeys;
  prefix?: (isSelected: boolean) => ReactNode;
  icon?: ReactNode;
  count?: number | string;
};

const TabComp: FC<TabProps> = ({ index, tabListId, isSelected, disabled, onClick, onKeyDown, title, icon, count, prefix }) => {
  const { t } = useTranslation();

  return (
    <button
      id={`tab-${tabListId}-${index}`}
      type='button'
      role='tab'
      aria-selected={isSelected}
      aria-controls={`tabpanel-${index}-${tabListId}`}
      tabIndex={isSelected ? 0 : -1}
      disabled={disabled}
      data-index={index}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={twMerge(
        'group inline-flex shrink-0 items-center gap-2 px-1 pb-3 focus:outline-none focus-visible:underline disabled:cursor-not-allowed',
        'border-b-2 aria-[selected=true]:border-brand-700 border-transparent',
        'motion-safe:transition-[border-color] motion-safe:duration-500 motion-reduce:transition-none'
      )}
    >
      {prefix?.(isSelected)}
      <div className='flex items-center gap-2'>
        {icon ? (
          <span
            className={twMerge(
              'text-quaternary-500 group-aria-[selected=true]:text-brand-700',
              'motion-safe:transition-colors motion-safe:duration-500 motion-reduce:transition-none'
            )}
          >
            {icon}
          </span>
        ) : null}
        <p
          className={twMerge(
            'first-letter:capitalize text-sm font-semibold group-aria-[selected=true]:text-brand-700 text-quaternary-500',
            'motion-safe:transition-colors motion-safe:duration-500 motion-reduce:transition-none'
          )}
        >
          {t(title as any)}
        </p>
        {count !== undefined ? (
          <span
            className={twMerge(
              'inline-flex min-w-5 justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold',
              'bg-secondary-background text-quaternary-500 group-aria-[selected=true]:bg-brand-100 group-aria-[selected=true]:text-brand-700'
            )}
          >
            {count}
          </span>
        ) : null}
      </div>
    </button>
  );
};

export const Tab = TabComp;
